import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { stringify } from "query-string";
import responseHandler from "../../middlewares/responseHandler";
import {
  ChangePasswordRequest,
  CreateParams,
  DeleteParams,
  ForgotRequest,
  IGetList,
  IResponse,
  LoginRequest,
  Params,
  RegisterRequest,
  ResetPasswordRequest,
  UpdateParams,
  UserResponse,
  VerifyRequest,
} from "../../interfaces/Types";
import { RootState } from "../../app/store";

const apiUrl = process.env.REACT_APP_BASE_API;

export const rawBaseQuery = fetchBaseQuery({
  baseUrl: apiUrl,
  prepareHeaders: (headers, { getState }) => {
    const token: string = (getState() as RootState).auth.token;
    const refreshToken: string = (getState() as RootState).auth.refreshToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    if (refreshToken) {
      headers.set("Refresh-Token", `${refreshToken}`);
    }
    return headers;
  },
});

const dynamicBaseQuery = async (args: any, api: any, extraOptions: any) => {
  const result = await responseHandler(args, api, extraOptions);

  return result;
};

export const ApiSlice = createApi({
  reducerPath: "api",
  baseQuery: dynamicBaseQuery,
  // cache behaviour
  keepUnusedDataFor: 0,

  endpoints: (builder) => ({
    login: builder.mutation<UserResponse, LoginRequest>({
      query: (credentials) => ({
        url: `${apiUrl}fixity/auth/login`,
        method: process.env.REACT_APP_BASE_METHOD_POST,
        body: credentials,
      }),
    }),
    verify: builder.mutation<UserResponse, VerifyRequest>({
      query: (credentials) => ({
        url: `${apiUrl}fixity/auth/login/verify2fa`,
        method: process.env.REACT_APP_BASE_METHOD_POST,
        body: credentials,
      }),
    }),
    register: builder.mutation<UserResponse, RegisterRequest>({
      query: (credentials) => ({
        url: `${apiUrl}fixity/auth/register`,
        method: process.env.REACT_APP_BASE_METHOD_POST,
        body: credentials,
      }),
    }),
    forgotPassword: builder.mutation<UserResponse, ForgotRequest>({
      query: (credentials) => ({
        url: `${apiUrl}${process.env.REACT_APP_FORGOT_PW_URL}`,
        method: process.env.REACT_APP_BASE_METHOD_POST,
        body: credentials,
      }),
    }),
    changePassword: builder.mutation<IResponse, ChangePasswordRequest>({
      query: (credentials) => ({
        url: `${apiUrl}${process.env.REACT_APP_CHANGE_PW_URL}`,
        method: process.env.REACT_APP_BASE_METHOD_POST,
        body: credentials,
      }),
    }),
    resetPassword: builder.mutation<IResponse, ResetPasswordRequest>({
      query: (credentials) => ({
        url: `${apiUrl}${process.env.REACT_APP_RESET_PW_URL}`,
        method: process.env.REACT_APP_BASE_METHOD_POST,
        body: credentials,
      }),
    }),

    logout: builder.query<any, any>({
      query: () => ({
        url: `${apiUrl}fixity/auth/logout`,
        method: process.env.REACT_APP_BASE_METHOD_GET,
      }),
    }),
    getList: builder.query<IResponse, IGetList>({
      query: ({ resource, params }) => {
        let query = {
          size: 100,
          pageNumber: 0,
        };
        if (params) {
          const { size, pageNumber } = params.pagination;
          query = {
            size: size,
            pageNumber: pageNumber,
            ...params.filter,
          };

          return `/${resource}?${stringify(query)}`;
        }
        return `/${resource}?${stringify(query)}`;
      },
      // response have to data property otherwise create new data prop
      transformResponse: (response) => {
        if ("data" in response) return response;
        return { data: response };
      },
    }),
    getOneById: builder.query<IResponse, Params>({
      query: ({ resource, id }) => `/${resource}/${id}`,
    }),
    getManyByIds: builder.query<IResponse, any>({
      query: ({ resource, params }) => {
        const query = {
          filter: JSON.stringify({ ids: params.ids }),
        };
        return `/${resource}?${stringify(query)}`;
      },
    }),
    create: builder.mutation<IResponse, CreateParams>({
      query: ({ resource, params }) => ({
        url: `/${resource}`,
        method: process.env.REACT_APP_BASE_METHOD_POST,
        body: params,
      }),
    }),
    update: builder.mutation<IResponse, UpdateParams>({
      query: ({ resource, params }) => ({
        url: `/${resource}`,
        method: process.env.REACT_APP_BASE_METHOD_PUT,
        body: params,
      }),
    }),
    delete: builder.mutation<IResponse, DeleteParams>({
      query: ({ resource, params }) => ({
        url: `/${resource}`,
        method: process.env.REACT_APP_BASE_METHOD_DELETE,
        body: params,
      }),
    }),
    get: builder.query<IResponse, Params>({
      query: ({ resource }) => `/${resource}`,
    }),
  }),
});

export const {
  useGetListQuery,
  useGetOneByIdQuery,
  useGetManyByIdsQuery,
  useLogoutQuery,
  useCreateMutation,
  useUpdateMutation,
  useDeleteMutation,
  useLoginMutation,
  useRegisterMutation,
  useForgotPasswordMutation,
  useChangePasswordMutation,
  useResetPasswordMutation,
  useGetQuery,
  useVerifyMutation,
} = ApiSlice;

export const {
  endpoints: { getList, getOneById, create, update, get },
} = ApiSlice;
